import { useState } from "react";
import type { VisitSlot } from "@school-sales-ai/shared";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useVisitSlots } from "@/hooks/useVisits";
import { formatMinuteOfDay } from "./visit-status";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Booking being moved — excluded server-side so it doesn't self-conflict. */
  excludeBookingId: string | null;
  isSaving: boolean;
  onSelect: (startsAtIso: string) => void;
};

function formatDayChip(localDate: string): string {
  // localDate is campus-local "YYYY-MM-DD"; noon UTC avoids TZ date shifts.
  return new Intl.DateTimeFormat("es-MX", {
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(new Date(`${localDate}T12:00:00Z`));
}

export function SlotPickerDialog({
  open,
  onOpenChange,
  excludeBookingId,
  isSaving,
  onSelect,
}: Props): React.JSX.Element {
  const { data, isLoading } = useVisitSlots(excludeBookingId, open);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<VisitSlot | null>(null);

  const slots = data?.slots ?? [];
  const days = [...new Set(slots.map((s) => s.localDate))];
  const activeDay = selectedDay && days.includes(selectedDay) ? selectedDay : days[0] ?? null;
  const daySlots = slots.filter((s) => s.localDate === activeDay);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Elegir nuevo horario</DialogTitle>
          <DialogDescription>
            Horarios con al menos un asesor disponible.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex flex-col gap-2 py-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-8 animate-pulse rounded bg-ink-100" />
            ))}
          </div>
        ) : slots.length === 0 ? (
          <p className="py-6 text-center text-sm text-ink-400">
            No hay horarios disponibles — revisa la disponibilidad de asesores.
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex gap-1.5 overflow-x-auto pb-1">
              {days.map((day) => (
                <button
                  key={day}
                  type="button"
                  onClick={() => {
                    setSelectedDay(day);
                    setSelectedSlot(null);
                  }}
                  className={cn(
                    "shrink-0 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                    day === activeDay
                      ? "border-moss bg-lime text-moss"
                      : "border-ink-200 text-ink-600 hover:bg-ink-050",
                  )}
                >
                  {formatDayChip(day)}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              {daySlots.map((slot) => (
                <button
                  key={slot.startsAt}
                  type="button"
                  onClick={() => setSelectedSlot(slot)}
                  className={cn(
                    "rounded-lg border px-2 py-2 text-[13px] font-medium transition-colors",
                    selectedSlot?.startsAt === slot.startsAt
                      ? "border-moss bg-lime text-moss"
                      : "border-ink-200 text-ink-700 hover:bg-ink-050",
                  )}
                >
                  {formatMinuteOfDay(slot.startMinute)}
                </button>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            size="sm"
            disabled={selectedSlot === null || isSaving}
            onClick={() => {
              if (selectedSlot) onSelect(selectedSlot.startsAt);
            }}
          >
            {isSaving ? "Guardando…" : "Confirmar horario"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
